import JSZip from "jszip";

import { EXTENSION_TO_MIME, MAX_ZIP_SIZE } from "@/domain/classification/uploadConstants";
import {
  getBasename,
  getExtension,
  isValidDocumentExtension,
} from "@/domain/classification/uploadFiles";

import type {
  ZipExtractionResult,
  ZipExtractionProgress,
} from "@/application/ports/services/IZipExtractor";
import { BusinessRuleError } from "@/domain/errors/BusinessRuleError";

export async function extractZip(
  file: File,
  onProgress?: (progress: ZipExtractionProgress) => void,
): Promise<ZipExtractionResult> {
  if (file.size > MAX_ZIP_SIZE) {
    throw new BusinessRuleError("ZIP_TOO_LARGE", "ZIP_TOO_LARGE");
  }

  onProgress?.({ phase: "loading", current: 0, total: 0 });

  const buffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(buffer);

  const entries: { name: string; zipObject: JSZip.JSZipObject }[] = [];
  zip.forEach((relativePath, zipObject) => {
    if (zipObject.dir) return;
    // Skip macOS resource fork files (._filename) and __MACOSX metadata
    const basename = getBasename(relativePath);
    if (basename.startsWith("._") || relativePath.includes("__MACOSX/")) return;
    entries.push({ name: relativePath, zipObject });
  });

  const validEntries = entries.filter((e) => isValidDocumentExtension(e.name));
  const ignoredCount = entries.length - validEntries.length;

  const validFiles: File[] = [];
  for (const [i, entry] of validEntries.entries()) {
    onProgress?.({
      phase: "extracting",
      current: i + 1,
      total: validEntries.length,
    });

    const blob = await entry.zipObject.async("blob");
    const basename = getBasename(entry.name);
    const ext = getExtension(basename);
    const mimeType = EXTENSION_TO_MIME[ext] ?? "application/octet-stream";

    validFiles.push(new File([blob], basename, { type: mimeType }));
  }

  return {
    validFiles,
    ignoredCount,
    totalEntries: entries.length,
  };
}
